package com.bikompanion.ui.playground

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.BiasAlignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.glass.ChromaticAberrationMode
import dev.chrisbanes.haze.glass.GlassOptics
import dev.chrisbanes.haze.glass.GlassStyle
import dev.chrisbanes.haze.glass.OpticalSizeValue
import dev.chrisbanes.haze.glass.hazeGlass
import dev.chrisbanes.haze.hazeSource
import dev.chrisbanes.haze.rememberHazeState
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewSurface
import kotlin.math.round
import kotlin.math.roundToInt

/**
 * Dev-only sandbox for tuning [GlassStyle] values against a live-rendered panel, using its own
 * backdrop as the Haze source. Not part of any app screen or nav graph — wire it up temporarily
 * (e.g. from a platform `main`/Activity) for a self-contained check. To check the effect against
 * real app content instead (e.g. the map), use [GlassPlaygroundOverlay] with an existing
 * [HazeState].
 */
@Composable
fun GlassPlaygroundScreen(modifier: Modifier = Modifier) {
    val hazeState = rememberHazeState()
    Box(modifier = modifier.fillMaxSize()) {
        // hazeSource lives on this Box alone, not the overlay below: if the glass panel were
        // inside it too, hazeSource would capture the panel along with the backdrop, and the
        // panel would end up blurring itself (see StationDetailsSheet.kt for the same trap).
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(PlaygroundBackdrop)
                .hazeSource(hazeState),
        ) {
            PlaygroundBackdropContent()
        }
        GlassPlaygroundOverlay(hazeState = hazeState, modifier = Modifier.fillMaxSize())
    }
}

/**
 * Draggable glass panel + tuning sliders, drawn against an existing [hazeState] instead of owning
 * its own backdrop — drop this on top of a real screen (behind which [hazeState] is already
 * capturing content via `hazeSource`) to check the effect against real content, e.g. the map.
 */
@Composable
fun GlassPlaygroundOverlay(
    hazeState: HazeState,
    modifier: Modifier = Modifier,
) {
    var blurRadius by remember { mutableFloatStateOf(7f) }
    var refractionStrength by remember { mutableFloatStateOf(1f) }
    var refractionHeightFraction by remember { mutableFloatStateOf(0.25f) }
    var refractionDisplacement by remember { mutableFloatStateOf(40f) }
    var refractionFoldStrength by remember { mutableFloatStateOf(0f) }
    var depth by remember { mutableFloatStateOf(1f) }
    var chromaticAberrationStrength by remember { mutableFloatStateOf(1f) }
    var chromaticAberrationFull by remember { mutableStateOf(true) }
    var chromaMultiplier by remember { mutableFloatStateOf(2f) }
    var specularIntensity by remember { mutableFloatStateOf(0.4f) }
    var specularExponent by remember { mutableFloatStateOf(24f) }
    var fresnelExponent by remember { mutableFloatStateOf(3f) }
    var ambientResponse by remember { mutableFloatStateOf(0.46f) }
    var contrast by remember { mutableFloatStateOf(0f) }
    var whitePoint by remember { mutableFloatStateOf(0f) }
    var alpha by remember { mutableFloatStateOf(1f) }
    var edgeSoftness by remember { mutableFloatStateOf(2f) }
    var lightPositionX by remember { mutableFloatStateOf(0f) }
    var lightPositionY by remember { mutableFloatStateOf(0f) }
    // Haze starts every glass node on a non-shader fallback renderer and only promotes to the
    // real blur shader once it decides it can afford to (GlassRuntimeEffect.updateDelegate).
    // The fallback path draws nothing at all when backgroundColor/tint are left fully transparent
    // (FallbackGlassDelegate.draw: `if (backgroundColor.alpha > 0f) { ...draw input... }`), so a
    // visible alpha here is required for the panel to render on *either* path, not just cosmetic.
    var backgroundAlpha by remember { mutableFloatStateOf(0.2f) }
    var tintAlpha by remember { mutableFloatStateOf(0f) }
    var panelOffset by remember { mutableStateOf(Offset.Zero) }

    val panelShape = RoundedCornerShape(28.dp)
    val glassStyle = GlassStyle {
        backgroundColor(Color.White.copy(alpha = backgroundAlpha))
        tint(Color.White.copy(alpha = tintAlpha))
        optics(
            GlassOptics(
                refractionStrength = refractionStrength,
                refractionHeightFraction = refractionHeightFraction,
                refractionDisplacement = refractionDisplacement.dp,
                depth = OpticalSizeValue.Fixed(depth),
                blurRadius = OpticalSizeValue.Fixed(blurRadius.dp),
                refractionFoldStrength = refractionFoldStrength,
            ),
        )
        shape(panelShape)
        chromaticAberrationStrength(chromaticAberrationStrength)
        chromaticAberrationMode(
            if (chromaticAberrationFull) ChromaticAberrationMode.Full else ChromaticAberrationMode.Simple,
        )
        chromaMultiplier(chromaMultiplier)
        specularIntensity(specularIntensity)
        specularExponent(specularExponent)
        fresnelExponent(fresnelExponent)
        ambientResponse(ambientResponse)
        contrast(contrast)
        whitePoint(whitePoint)
        alpha(alpha)
        edgeSoftness(edgeSoftness.dp)
        lightPosition(BiasAlignment(horizontalBias = lightPositionX, verticalBias = lightPositionY))
    }

    Row(modifier = modifier) {
        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight(),
        ) {
            // No background/hazeSource here: this overlay draws against whatever hazeState is
            // already capturing (e.g. HomeScreen's map), not a backdrop of its own.
            Box(
                modifier = Modifier
                    .align(Alignment.Center)
                    .offset { IntOffset(panelOffset.x.roundToInt(), panelOffset.y.roundToInt()) }
                    .size(260.dp)
                    .clip(panelShape)
                    .hazeGlass(input = HazeInput.Sources(hazeState), style = glassStyle)
                    .pointerInput(Unit) {
                        detectDragGestures { change, dragAmount ->
                            change.consume()
                            panelOffset += dragAmount
                        }
                    },
            )
        }
        Column(
            modifier = Modifier
                .width(320.dp)
                .fillMaxHeight()
                .background(MaterialTheme.colorScheme.surface)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            Text("Glass Playground", style = MaterialTheme.typography.titleMedium)
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            FloatSlider("backgroundColor alpha", backgroundAlpha, 0f..1f) { backgroundAlpha = it }
            FloatSlider("tint alpha", tintAlpha, 0f..1f) { tintAlpha = it }
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            FloatSlider("blurRadius", blurRadius, 0f..40f) { blurRadius = it }
            FloatSlider("refractionStrength", refractionStrength, 0f..1f) { refractionStrength = it }
            FloatSlider("refractionHeightFraction", refractionHeightFraction, 0f..1f) { refractionHeightFraction = it }
            FloatSlider("refractionDisplacement", refractionDisplacement, 0f..80f) { refractionDisplacement = it }
            FloatSlider("refractionFoldStrength", refractionFoldStrength, 0f..1f) { refractionFoldStrength = it }
            FloatSlider("depth", depth, 0f..1f) { depth = it }
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            FloatSlider("chromaticAberrationStrength", chromaticAberrationStrength, 0f..1f) { chromaticAberrationStrength = it }
            BooleanRow("chromaticAberrationMode = Full", chromaticAberrationFull) { chromaticAberrationFull = it }
            FloatSlider("chromaMultiplier", chromaMultiplier, 0f..2f) { chromaMultiplier = it }
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            FloatSlider("specularIntensity", specularIntensity, 0f..1f) { specularIntensity = it }
            FloatSlider("specularExponent", specularExponent, 0f..64f) { specularExponent = it }
            FloatSlider("fresnelExponent", fresnelExponent, 0f..12f) { fresnelExponent = it }
            FloatSlider("ambientResponse", ambientResponse, 0f..1f) { ambientResponse = it }
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            FloatSlider("contrast", contrast, -1f..1f) { contrast = it }
            FloatSlider("whitePoint", whitePoint, -1f..1f) { whitePoint = it }
            FloatSlider("alpha", alpha, 0f..1f) { alpha = it }
            FloatSlider("edgeSoftness", edgeSoftness, 0f..20f) { edgeSoftness = it }
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            FloatSlider("lightPosition.x", lightPositionX, -1f..1f) { lightPositionX = it }
            FloatSlider("lightPosition.y", lightPositionY, -1f..1f) { lightPositionY = it }
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
            val code = remember(
                backgroundAlpha,
                tintAlpha,
                blurRadius,
                refractionStrength,
                refractionHeightFraction,
                refractionDisplacement,
                refractionFoldStrength,
                depth,
                chromaticAberrationStrength,
                chromaticAberrationFull,
                chromaMultiplier,
                specularIntensity,
                specularExponent,
                fresnelExponent,
                ambientResponse,
                contrast,
                whitePoint,
                alpha,
                edgeSoftness,
                lightPositionX,
                lightPositionY,
            ) {
                generateGlassStyleCode(
                    backgroundAlpha = backgroundAlpha,
                    tintAlpha = tintAlpha,
                    blurRadius = blurRadius,
                    refractionStrength = refractionStrength,
                    refractionHeightFraction = refractionHeightFraction,
                    refractionDisplacement = refractionDisplacement,
                    refractionFoldStrength = refractionFoldStrength,
                    depth = depth,
                    chromaticAberrationStrength = chromaticAberrationStrength,
                    chromaticAberrationFull = chromaticAberrationFull,
                    chromaMultiplier = chromaMultiplier,
                    specularIntensity = specularIntensity,
                    specularExponent = specularExponent,
                    fresnelExponent = fresnelExponent,
                    ambientResponse = ambientResponse,
                    contrast = contrast,
                    whitePoint = whitePoint,
                    alpha = alpha,
                    edgeSoftness = edgeSoftness,
                    lightPositionX = lightPositionX,
                    lightPositionY = lightPositionY,
                )
            }

            // LocalClipboard's ClipEntry needs a platform-native transferable object per target,
            // which isn't worth an expect/actual just for this debug screen's copy button.
            @Suppress("DEPRECATION")
            val clipboardManager = LocalClipboardManager.current
            Button(onClick = { clipboardManager.setText(AnnotatedString(code)) }) {
                Text("Copy GlassStyle code")
            }
            SelectionContainer {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 120.dp)
                        .background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(8.dp))
                        .padding(8.dp),
                ) {
                    Text(
                        text = code,
                        fontFamily = FontFamily.Monospace,
                        style = MaterialTheme.typography.bodySmall,
                    )
                }
            }
        }
    }
}

private fun round2(value: Float): Float = round(value * 100) / 100f

/** Emits a [GlassStyle] block matching the current slider values, ready to paste into real code. */
private fun generateGlassStyleCode(
    backgroundAlpha: Float,
    tintAlpha: Float,
    blurRadius: Float,
    refractionStrength: Float,
    refractionHeightFraction: Float,
    refractionDisplacement: Float,
    refractionFoldStrength: Float,
    depth: Float,
    chromaticAberrationStrength: Float,
    chromaticAberrationFull: Boolean,
    chromaMultiplier: Float,
    specularIntensity: Float,
    specularExponent: Float,
    fresnelExponent: Float,
    ambientResponse: Float,
    contrast: Float,
    whitePoint: Float,
    alpha: Float,
    edgeSoftness: Float,
    lightPositionX: Float,
    lightPositionY: Float,
): String =
    buildString {
        appendLine("GlassStyle {")
        appendLine("    backgroundColor(Color.White.copy(alpha = ${round2(backgroundAlpha)}f))")
        appendLine("    tint(Color.White.copy(alpha = ${round2(tintAlpha)}f))")
        appendLine("    optics(")
        appendLine("        GlassOptics(")
        appendLine("            refractionStrength = ${round2(refractionStrength)}f,")
        appendLine("            refractionHeightFraction = ${round2(refractionHeightFraction)}f,")
        appendLine("            refractionDisplacement = ${round2(refractionDisplacement)}.dp,")
        appendLine("            depth = OpticalSizeValue.Fixed(${round2(depth)}f),")
        appendLine("            blurRadius = OpticalSizeValue.Fixed(${round2(blurRadius)}.dp),")
        appendLine("            refractionFoldStrength = ${round2(refractionFoldStrength)}f,")
        appendLine("        ),")
        appendLine("    )")
        appendLine("    shape(SheetShape)")
        appendLine("    chromaticAberrationStrength(${round2(chromaticAberrationStrength)}f)")
        appendLine(
            "    chromaticAberrationMode(ChromaticAberrationMode." +
                (if (chromaticAberrationFull) "Full" else "Simple") + ")",
        )
        appendLine("    chromaMultiplier(${round2(chromaMultiplier)}f)")
        appendLine("    specularIntensity(${round2(specularIntensity)}f)")
        appendLine("    specularExponent(${round2(specularExponent)}f)")
        appendLine("    fresnelExponent(${round2(fresnelExponent)}f)")
        appendLine("    ambientResponse(${round2(ambientResponse)}f)")
        appendLine("    contrast(${round2(contrast)}f)")
        appendLine("    whitePoint(${round2(whitePoint)}f)")
        appendLine("    alpha(${round2(alpha)}f)")
        appendLine("    edgeSoftness(${round2(edgeSoftness)}.dp)")
        appendLine(
            "    lightPosition(BiasAlignment(horizontalBias = ${round2(lightPositionX)}f, " +
                "verticalBias = ${round2(lightPositionY)}f))",
        )
        append("}")
    }

/** Colorful, high-contrast backdrop so refraction/chromatic-aberration/specular changes read clearly. */
private val PlaygroundBackdrop = Brush.sweepGradient(
    listOf(Color(0xFFFF5F6D), Color(0xFFFFC371), Color(0xFF00C9A7), Color(0xFF6A82FB), Color(0xFFFF5F6D)),
)

/**
 * A flat gradient alone barely shows blur/refraction since there's nothing sharp to distort — this
 * tiles bold text and solid shapes across the backdrop so dragging the glass panel over it makes
 * every effect (blur softening edges, refraction bending lines, chromatic aberration fringing them)
 * obvious.
 */
@Composable
private fun PlaygroundBackdropContent() {
    Column(modifier = Modifier.fillMaxSize()) {
        repeat(6) { row ->
            Row(modifier = Modifier.fillMaxWidth().weight(1f)) {
                repeat(4) { column ->
                    val isShape = (row + column) % 2 == 0
                    Box(
                        modifier = Modifier.weight(1f).fillMaxHeight(),
                        contentAlignment = Alignment.Center,
                    ) {
                        if (isShape) {
                            Box(
                                modifier = Modifier
                                    .size(48.dp)
                                    .background(Color.Black.copy(alpha = 0.8f), CircleShape),
                            )
                        } else {
                            Text(
                                text = "GLASS",
                                color = Color.White,
                                fontSize = 22.sp,
                                fontWeight = FontWeight.Black,
                                textAlign = TextAlign.Center,
                            )
                        }
                    }
                }
            }
        }
    }
}

// Dev-only playground: the value-based Slider is deprecated in Material 3 1.13, but moving to the
// SliderState overload buys nothing for a throwaway tuning screen.
@Suppress("DEPRECATION")
@Composable
private fun FloatSlider(
    label: String,
    value: Float,
    range: ClosedFloatingPointRange<Float>,
    onValueChange: (Float) -> Unit,
) {
    Column {
        Text("$label: ${round2(value)}", style = MaterialTheme.typography.labelMedium)
        Slider(value = value, onValueChange = onValueChange, valueRange = range)
    }
}

@Composable
private fun BooleanRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Checkbox(checked = checked, onCheckedChange = onCheckedChange)
        Text(label, style = MaterialTheme.typography.labelMedium)
    }
}

@PreviewLightDarkCompact
@Composable
private fun GlassPlaygroundScreenPreview() {
    PreviewSurface {
        GlassPlaygroundScreen()
    }
}
